// src/utils/boqMatch.js
// P4-3 (30 Oct 2026, D-319) - which Item Master item a BOQ / AI BOQ row is, and
// what happens to that row on save, in ONE place.
//
// WHY THIS FILE EXISTS
// The BOQ upload used a last-wins name map, so of two same-name items it took
// whichever came last. The AI function's matched_item_id comes from the same
// kind of map, so an AI match cannot be trusted either. Now:
//   - a name goes through matchItem(): exactly one item -> linked, 2+ -> the
//     user picks from the candidates or skips the row. Never a guess.
//   - an AI id is only a hint: accepted when that one item has the row's name
//     key and no other active item has it. A different name -> manual pick,
//     with the AI's item first (Ravi 30 Oct, decision 1).
//   - words in another order ("Piping Copper" / "Copper Piping"): createItem()
//     would accept it as new, so it is a SUGGESTION in the manual pick. Never an
//     auto-link and never a new item (finding d).
//   - no budget line without an item_id (decision 2): an ambiguous row is picked
//     or skipped, and a skipped row is listed after the import.
//   - no match at all -> a new item through createItem(), as before.
// Pure (no Firebase) -> covered by src/utils/boqMatch.test.js (Jest).

import { itemNameKey, matchItem } from "./itemMatch";
import { pickerItems, itemOptionLabel } from "./itemPicker";

const clean = (v) => String(v ?? "").trim();

// "Copper-Piping 250 mm" and "250mm Piping Copper" -> one key.
export const wordSortedKey = (name) =>
  itemNameKey(name).replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean).sort().join(" ");

/**
 * The list every resolve below reads: own + shared "" items, active only,
 * `_blocked` marks included. Built once per file, not once per row.
 */
export const matchList = (items, companyId) =>
  pickerItems(items, companyId).map((it) => ({ ...it, _wordKey: wordSortedKey(it.item_name) }));

const NOT_FOUND = { status: "not_found", candidates: [] };

const ambiguous = (reason, candidates, ai_item = null) => ({ status: "ambiguous", reason, candidates, ai_item });

const uniqueById = (items) => {
  const seen = new Set();
  return items.filter((it) => {
    if (seen.has(it.id)) return false;
    seen.add(it.id);
    return true;
  });
};

const similarNameItems = (list, name) => {
  const key = wordSortedKey(name);
  return key ? list.filter((m) => m._wordKey === key) : [];
};

/**
 * @param {Array}  list  from matchList()
 * @param {Object} row   { item_name }
 * @returns {{status: "linked", item: Object, candidates: Array}
 *         | {status: "ambiguous", reason: string, candidates: Array, ai_item: Object|null}
 *         | {status: "not_found", candidates: Array}}
 */
export function resolveBoqRow(list, row) {
  const name = row?.item_name;
  if (!itemNameKey(name)) return NOT_FOUND;
  const r = matchItem(list, { item_name: name });
  if (r.item) {
    return r.item._blocked
      ? ambiguous("blocked", [r.item])
      : { status: "linked", item: r.item, candidates: [] };
  }
  if (r.status === "ambiguous") return ambiguous("same_name", r.candidates);
  const similar = similarNameItems(list, name);
  return similar.length > 0 ? ambiguous("similar_name", similar) : NOT_FOUND;
}

/**
 * Re-checks the AI function's matched_item_id. An id that is missing, inactive,
 * of another company (none of these are in the list) or only a doc id counts as
 * no AI match, so the row is resolved by its name.
 * @param {Array}  list  from matchList()
 * @param {Object} row   { item_name, matched_item_id }
 */
export function resolveAiRow(list, row) {
  const id = clean(row?.matched_item_id);
  if (!id) return resolveBoqRow(list, row);

  const r = matchItem(list, { item_id: id });
  if (r.status === "ambiguous") return ambiguous("blocked", r.candidates);
  if (!r.item || r.how === "doc_id") return resolveBoqRow(list, row);
  if (r.item._blocked) return ambiguous("blocked", [r.item]);

  const key = itemNameKey(row?.item_name);
  const sameName = list.filter((m) => itemNameKey(m.item_name) === key);
  if (itemNameKey(r.item.item_name) === key) {
    return sameName.length === 1
      ? { status: "linked", item: r.item, candidates: [] }
      : ambiguous("same_name", uniqueById([r.item, ...sameName]), r.item);
  }
  return ambiguous("ai_name_differs", uniqueById([r.item, ...sameName, ...similarNameItems(list, row?.item_name)]), r.item);
}

// The picked item, only if it is still one pickable item.
const pickedItem = (list, itemId) => {
  const id = clean(itemId);
  if (!id) return null;
  const r = matchItem(list, { item_id: id });
  return r.item && r.how !== "doc_id" && !r.item._blocked ? r.item : null;
};

/**
 * What happens to one row on save.
 * @param {Object}  row  { match, mapTo (picked item_id), skip, createNew }
 * @param {Array}   list from matchList()
 * @param {Object}  [opts]
 * @param {boolean} [opts.autoNew] AI BOQ: no match -> new item without a tick
 * @returns {{kind: "linked", item: Object}|{kind: "new"}|{kind: "skip", reason: string}|{kind: "open"}}
 */
export function rowOutcome(row, list, { autoNew = false } = {}) {
  const match = row?.match;
  const pick  = clean(row?.mapTo);
  if (match?.status === "ambiguous") {
    if (row.skip) return { kind: "skip", reason: matchReasonText(match) };
    const item = pickedItem(list, pick);
    const isCandidate = !!item && match.candidates.some((c) => c.item_id === item.item_id);
    return isCandidate ? { kind: "linked", item } : { kind: "open" };
  }
  // A pick or skip left over from before a re-check, on a row that is no longer
  // ambiguous, is stale: the row stops for a fresh look. It is never linked to
  // another item than the one picked, and never turned into a new item.
  if (match?.status === "linked") {
    const stale = row?.skip || (pick && pick !== clean(match.item.item_id));
    return stale ? { kind: "open" } : { kind: "linked", item: match.item };
  }
  if (autoNew) return pick || row?.skip ? { kind: "open" } : { kind: "new" };
  if (pick) {
    const item = pickedItem(list, pick);
    return item ? { kind: "linked", item } : { kind: "open" };
  }
  return row?.createNew ? { kind: "new" } : { kind: "open" };
}

/**
 * BOQ upload: splits the preview's rows for saving. A stage with no rows left is
 * dropped, so "Replace existing" can never empty a stage by accident.
 * @returns {{ groups: Array, skipped: Array, unresolved: Array }}
 */
export function decideBoqRows(groups, list) {
  const skipped = [];
  const unresolved = [];
  const kept = (Array.isArray(groups) ? groups : []).map((group) => ({
    ...group,
    rows: group.rows.flatMap((row) => {
      const out = rowOutcome(row, list);
      if (out.kind === "skip") {
        skipped.push({ stage_name: group.stage_name, item_name: row.item_name, reason: out.reason });
        return [];
      }
      if (out.kind === "open") {
        unresolved.push({ stage_name: group.stage_name, item_name: row.item_name });
        return [];
      }
      return [out.kind === "linked"
        ? { ...row, resolvedItem: out.item, matched: true }
        : { ...row, resolvedItem: { item_id: "", item_code: "", item_name: row.item_name, unit: row.unit } }];
    }),
  }));
  return { groups: kept.filter((g) => g.rows.length > 0), skipped, unresolved };
}

/**
 * New-item names that are the same words in another order. createItem() would
 * accept both spellings, so the save stops and names them.
 * @returns {string[][]} one list of names per clash
 */
export function newNameClashes(names) {
  const groups = new Map();
  for (const name of Array.isArray(names) ? names : []) {
    const key = wordSortedKey(name);
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, new Map());
    groups.get(key).set(itemNameKey(name), clean(name));
  }
  return [...groups.values()].filter((spellings) => spellings.size > 1).map((spellings) => [...spellings.values()]);
}

/**
 * Merge into an existing stage: an item already in the stage is not added again.
 * Compared by item_id (two same-name items are two items). An old line with no
 * item_id can only be compared by its name.
 */
export function newBudgetLines(existing, incoming) {
  const lines = Array.isArray(existing) ? existing : [];
  const ids = new Set(lines.map((l) => clean(l.item_id)).filter(Boolean));
  const oldNames = new Set(lines.filter((l) => !clean(l.item_id)).map((l) => itemNameKey(l.item_name)));
  return (Array.isArray(incoming) ? incoming : []).filter((l) => {
    const id = clean(l.item_id);
    return !(id && ids.has(id)) && !oldNames.has(itemNameKey(l.item_name));
  });
}

// Screen text for an ambiguous row.
export function matchReasonText(match) {
  const n = match?.candidates?.length || 0;
  switch (match?.reason) {
    case "same_name":       return `${n} items match this name — pick one`;
    case "similar_name":    return "Same words in Item Master in another order — pick it, or skip this row";
    case "ai_name_differs": return "AI suggested an item with a different name — pick one";
    case "blocked":         return match.candidates?.[0]?._blocked || "This item cannot be picked";
    default:                return "";
  }
}

export function candidateLabel(match, item) {
  const label = itemOptionLabel(item);
  if (match?.ai_item?.item_id === item?.item_id) return `${label} — AI suggestion`;
  return match?.reason === "similar_name" ? `${label} — suggestion` : label;
}
